import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { adminApi } from '../services/api'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')

    if (!token) {
      setError('Reset token is missing. Please request a new link.')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setIsLoading(true)

    try {
      await adminApi.resetAdminPassword(token, password)
      navigate('/login', { replace: true, state: { resetSuccess: true } })
    } catch (err) {
      setError('Reset link is invalid or expired. Please request a new one.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--color-forest-dark)',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: '420px',
          background: 'var(--color-white)',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <div
            className="font-display"
            style={{
              color: 'var(--color-gold)',
              fontSize: '26px',
              fontWeight: 700,
              marginBottom: '4px',
            }}
          >
            Set New Password
          </div>
          <div
            className="font-body"
            style={{
              color: 'var(--color-muted)',
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            Choose a new password for your admin account.
          </div>
        </div>

        <input
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          placeholder="New password"
          autoComplete="new-password"
          required
          style={{
            width: '100%',
            height: '48px',
            border: '1.5px solid var(--color-border)',
            borderRadius: '10px',
            padding: '14px 16px',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px',
            marginBottom: '12px',
            color: 'var(--color-dark)',
          }}
        />

        <input
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          type="password"
          placeholder="Confirm password"
          autoComplete="new-password"
          required
          style={{
            width: '100%',
            height: '48px',
            border: '1.5px solid var(--color-border)',
            borderRadius: '10px',
            padding: '14px 16px',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px',
            marginBottom: '12px',
            color: 'var(--color-dark)',
          }}
        />

        {error ? (
          <p
            className="font-body"
            style={{
              marginTop: '-6px',
              marginBottom: '12px',
              fontSize: '13px',
              color: 'var(--color-error)',
            }}
          >
            {error}
          </p>
        ) : null}

        <button
          disabled={isLoading}
          type="submit"
          className="font-body"
          style={{
            width: '100%',
            height: '48px',
            border: 'none',
            borderRadius: '999px',
            background: 'var(--color-gold)',
            color: 'var(--color-dark)',
            fontSize: '15px',
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.9 : 1,
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={(event) => {
            if (!isLoading) {
              event.currentTarget.style.opacity = '0.9'
            }
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.opacity = isLoading ? '0.9' : '1'
          }}
        >
          {isLoading ? 'Updating...' : 'Update password'}
        </button>

        <div style={{ marginTop: '16px', textAlign: 'center' }}>
          <Link
            to="/login"
            className="font-body"
            style={{
              fontSize: '13px',
              color: 'var(--color-muted)',
              textDecoration: 'none',
            }}
          >
            Back to login
          </Link>
        </div>
      </form>
    </div>
  )
}
